import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ShoppingCart, 
  Trash2, 
  ArrowRight, 
  ShieldCheck, 
  ChevronLeft,
  CreditCard,
  Zap
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import api from "../services/api";
import { getToken } from "../services/auth";
import PublicNavbar from "../components/PublicNavbar";

export default function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem("cart") || "[]");
    setCartItems(items);
  }, []);

  const removeItem = (id) => {
    const updated = cartItems.filter(item => item.id !== id);
    setCartItems(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Item removed from cart");
  };

  const calculateTotal = () => {
    return cartItems.reduce((acc, item) => acc + (item.price || 0), 0);
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async () => {
    const token = getToken();
    if (!token) {
      toast.error("Please login to complete purchase");
      navigate("/login?redirect=cart");
      return;
    }

    setLoading(true);
    try {
      const res = await loadRazorpay();
      if (!res) {
        toast.error("Razorpay SDK failed to load. Are you online?");
        return;
      }

      // 1. Create order on backend (Corrected URL prefix to /payments) and fetch config key
      const total = calculateTotal();
      const [orderRes, configRes] = await Promise.all([
        api.post("/payments/create-order", { amount: total }),
        api.get("/payments/config").catch(() => ({ data: {} }))
      ]);
      const order = orderRes.data;
      const rzpKey = configRes.data.razorpay_key || "rzp_test_ShJUkFoOhGho5K";

      // 2. Open Razorpay Checkout
      const options = {
        key: rzpKey,
        amount: order.amount,
        currency: "INR",
        name: "Antigravity Learning",
        description: "Course Purchase",
        order_id: order.id,
        handler: async (response) => {
          try {
            // 3. Verify payment on backend (Corrected URL prefix to /payments)
            await api.post("/payments/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              item_ids: cartItems.map(i => i.id)
            });

            localStorage.setItem("cart", "[]");
            window.dispatchEvent(new Event("cartUpdated"));
            toast.success("Payment successful! Access granted.");
            navigate("/dashboard");
          } catch (err) {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        prefill: {
          name: "User", // Can be dynamic
          email: "user@example.com",
        },
        theme: {
          color: "#4f46e5",
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error(err);
      toast.error("Error during checkout initiation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      <PublicNavbar />

      <main className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
        <div className="flex items-center gap-4 mb-12">
          <button onClick={() => navigate(-1)} className="p-3 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 transition-all">
            <ChevronLeft size={20} className="dark:text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Your Shopping Cart</h1>
            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1">{cartItems.length} items ready for checkout</p>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-20 text-center border border-slate-100 dark:border-slate-800 shadow-sm">
            <div className="w-24 h-24 bg-slate-50 dark:bg-slate-800 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8">
              <ShoppingCart size={40} className="text-slate-300" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-4">Your cart is empty</h2>
            <p className="text-slate-500 font-medium mb-10 max-w-sm mx-auto">Looks like you haven't added any courses or test series to your cart yet.</p>
            <Link 
              to="/public-courses" 
              className="inline-flex items-center gap-3 px-10 py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all"
            >
              Browse Courses <ArrowRight size={20} />
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Items List */}
            <div className="lg:col-span-2 space-y-6">
              <AnimatePresence>
                {cartItems.map((item) => (
                  <motion.div 
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    key={item.id} 
                    className="p-6 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-center gap-8 relative group hover:shadow-xl transition-all"
                  >
                    <div className="w-full md:w-40 aspect-video rounded-3xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                      <img 
                        src={item.thumbnail || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80"} 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                      />
                    </div>
                    
                    <div className="flex-1 text-center md:text-left">
                      <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-2">{item.category || "General"}</p>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2 line-clamp-1">{item.title}</h3>
                      <div className="flex items-center justify-center md:justify-start gap-4 text-slate-400 text-xs font-bold">
                         <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Full Access</span>
                         <span className="flex items-center gap-1.5"><Zap size={14} /> Lifetime Updates</span>
                      </div>
                    </div>

                    <div className="text-center md:text-right shrink-0">
                      <p className="text-2xl font-black text-slate-900 dark:text-white mb-4">₹{item.price}</p>
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="p-4 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl transition-all"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Summary Card */}
            <div className="lg:col-span-1">
              <div className="bg-slate-900 text-white rounded-[3rem] p-10 shadow-2xl sticky top-28 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-600/20 blur-3xl" />
                
                <h3 className="text-2xl font-black mb-10 flex items-center gap-3">
                  <CreditCard className="text-indigo-400" /> Order Summary
                </h3>
                
                <div className="space-y-6 mb-10">
                  <div className="flex items-center justify-between text-slate-400 font-bold uppercase tracking-widest text-xs">
                    <span>Subtotal</span>
                    <span className="text-white">₹{calculateTotal()}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-400 font-bold uppercase tracking-widest text-xs">
                    <span>GST (18%)</span>
                    <span className="text-white">Included</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-black uppercase tracking-tighter">Total Payable</span>
                    <span className="text-3xl font-black text-indigo-400 tracking-tight">₹{calculateTotal()}</span>
                  </div>
                </div>

                <button 
                  disabled={loading}
                  onClick={handleCheckout}
                  className="w-full py-5 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Complete Checkout <ArrowRight size={20} /></>
                  )}
                </button>

                <p className="text-[10px] text-center text-slate-500 font-black uppercase tracking-widest mt-8">
                  Secured by Razorpay • Instant Access
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
